import { trackEvent } from "jose_modern/analytics";
import { onViewLoad } from "jose_modern/on_view_load";
import { once } from "jose_modern/once";
import { bindDimensions } from "~/label/dimensions";
import { bindPreviewButton } from "~/label/preview_button";
import { Modal } from "~/jose_modern/modal";
import { initNameModal } from "./recipe_name_modal.js";

let altDownloadButtonCleanup = null;
let altDownloadButtonTimeout = null;
let analyticsInitialized = false;

function validateZoomValue(zoomInput) {
  const zoomValue = parseFloat(zoomInput.value);
  const min = parseFloat(zoomInput.min || 0.8);
  const max = parseFloat(zoomInput.max || 6.0);

  if (isNaN(zoomValue) || zoomValue < min || zoomValue > max) {
    zoomInput.setCustomValidity(`Zoom must be between ${min} and ${max}`);
    return false;
  }

  zoomInput.setCustomValidity("");
  return true;
}

function bindZoomValidation() {
  const zoomInput = document.getElementById("zoom");
  const imageForm = document.getElementById("render-image");
  const downloadButton = imageForm?.querySelector("button");

  if (!zoomInput || !imageForm || !downloadButton) return;

  function updateButtonState() {
    const isValid = validateZoomValue(zoomInput);
    downloadButton.disabled = !isValid;

    // Report validity only when the input has been interacted with
    if (zoomInput.dataset.interacted === "true") {
      zoomInput.reportValidity();
    }
  }

  zoomInput.addEventListener("input", function () {
    this.dataset.interacted = "true";
    updateButtonState();
  });

  zoomInput.addEventListener("focus", function () {
    this.dataset.interacted = "true";
  });

  updateButtonState();
}

function bindStashedFileListeners() {
  // watches for stashed file activity on the label page and updates
  // #avery-modal stashed-file download status
  const statusElement = document.querySelector("#stashed-file-status-mirror");

  if (!statusElement) {
    return;
  }

  document.addEventListener("stashed-file:start", () =>
    statusElement.classList.remove("is-hidden")
  );
  document.addEventListener("stashed-file:ready", () => statusElement.classList.add("is-hidden"));
}

function bindAnalytics() {
  if (analyticsInitialized) {
    return;
  }
  analyticsInitialized = true;

  const labelActions = document.querySelector(".label-actions");
  if (labelActions) {
    const isPublic = labelActions.dataset.recipePublic === "true";
    const recipeId = labelActions.dataset.recipeId;

    if (isPublic) {
      const signUpLinks = document.querySelectorAll('a[href*="/sign_up"]');
      signUpLinks.forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set("source", "public_recipe");

        if (recipeId) {
          url.searchParams.set("recipe_id", recipeId);
        }

        link.href = url.toString();
      });
    }
  }

  document.addEventListener("click", function (event) {
    // Handle download button clicks
    const button = event.target.closest("button");
    if (button && !button.disabled) {
      const form = button.closest("form[data-form-name]");
      if (form) {
        const formName = form.dataset.formName;
        if (formName === "render-pdf" || formName === "render-image") {
          const labelActions =
            form.closest(".label-actions") || document.querySelector(".label-actions");
          if (!labelActions) {
            return;
          }

          const downloadFormat = formName === "render-pdf" ? "pdf" : "png";
          const isPublic = labelActions.dataset.recipePublic === "true";

          if (isPublic) {
            const eventData = {
              label_type: downloadFormat,
              recipe_id: labelActions.dataset.recipeId,
              recipe_name: labelActions.dataset.recipeName,
              seo_slug: labelActions.dataset.recipeSeoSlug,
            };
            trackEvent("public_download_label", eventData);
          } else {
            trackEvent("download_label", { label_type: downloadFormat });
          }
          return;
        }
      }
    }

    const signUpLink = event.target.closest('a[href*="/sign_up"]');
    if (signUpLink) {
      const labelActions = document.querySelector(".label-actions");
      if (!labelActions) {
        return;
      }

      const isPublic = labelActions.dataset.recipePublic === "true";

      if (isPublic) {
        const eventData = {
          source: "public_recipe",
          recipe_id: labelActions.dataset.recipeId,
        };

        trackEvent("public_recipe_sign_up_clicked", eventData);
      }
    }
  });
}

function bindLabelActions(container) {
  if (!container) return;

  const renderSelect = container.querySelector(".js-render-select");
  const downloadForms = container.querySelectorAll(".js-download-form");

  if (!renderSelect || downloadForms.length === 0) {
    return;
  }

  const watermarkUpgrade = container.querySelector(".js-watermark-upgrade");

  const updateVisibility = () => {
    const selectedValue = renderSelect.value;
    downloadForms.forEach((form) => {
      const formName = form.getAttribute("data-form-name");
      form.classList.toggle("is-hidden", formName !== selectedValue);
    });
    if (watermarkUpgrade) {
      watermarkUpgrade.classList.toggle("is-hidden", selectedValue === "embed_label_form");
    }
  };

  renderSelect.addEventListener("change", updateVisibility);

  // Set initial state
  updateVisibility();
}

/**
 * Alternative download button->modal next to #labeltype select element.
 *
 * The alt button is shown if the main label_actions area goes off-screen,
 * and it opens a modal containing another instance of label_actions.
 */
function bindAltDownloadButton() {
  const state = {
    isReady: false,
    hasUserInteracted: false,
  };

  const elements = {
    labelAndClaimDiv: document.getElementById("label-and-claim-div"),
    labelActionsMain: document.getElementById("label-actions-main"), // The one below the label
    altTriggerButton: document.getElementById("label-actions-alt"), // Button next to #labeltype
  };

  if (!elements.labelActionsMain || !elements.altTriggerButton) {
    return;
  }

  // In test environment, keep main actions always visible but still allow alt button
  const isTestEnvironment = document.querySelector("#recipes-show.test-environment") !== null;

  function trackUserInteraction() {
    if (!state.hasUserInteracted) {
      state.hasUserInteracted = true;
    }
  }

  function showAlternativeUI() {
    elements.altTriggerButton.style.display = "inline-block";
    elements.altTriggerButton.style.visibility = "visible";

    if (!state.isReady || !state.hasUserInteracted) {
      elements.altTriggerButton.classList.add("immediate");
      elements.altTriggerButton.style.opacity = "1";
      setTimeout(() => elements.altTriggerButton.classList.remove("immediate"), 0);
    } else {
      elements.altTriggerButton.style.opacity = "1";
    }
  }

  function hideAlternativeUI() {
    elements.altTriggerButton.style.opacity = "0";
    setTimeout(() => {
      if (elements.altTriggerButton.style.opacity === "0") {
        elements.altTriggerButton.style.visibility = "hidden";
      }
    }, 300);
  }

  function showMainActions() {
    elements.labelActionsMain.style.visibility = "visible";
    elements.labelActionsMain.style.pointerEvents = "auto";

    if (!state.isReady || !state.hasUserInteracted) {
      elements.labelActionsMain.classList.add("immediate");
      elements.labelActionsMain.style.opacity = "1";
      setTimeout(() => elements.labelActionsMain.classList.remove("immediate"), 0);
    } else {
      elements.labelActionsMain.style.opacity = "1";
    }
  }

  /// Note: the reason for showing/hiding the label actions is not really
  /// because they _need_ to be hidden when they're off-screen, but moreso
  /// to subtly draw attention to the buttons - "hey, over here". Considered
  /// a highlight etc but thought alternatives were more distracting. Also,
  /// I think having both visible always feels like it's pulling you in different directions.
  function hideMainActions() {
    // In test environment, never hide main actions
    if (isTestEnvironment) return;

    elements.labelActionsMain.style.opacity = "0";
    elements.labelActionsMain.style.pointerEvents = "none";
    setTimeout(() => {
      if (elements.labelActionsMain.style.opacity === "0") {
        elements.labelActionsMain.style.visibility = "hidden";
      }
    }, 300);
  }

  function checkVisibilityStates() {
    const actionsRect = elements.labelActionsMain.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Although this is superficially not that different from IntersectionObserver, arrived at it after testing
    // and felt that the UX was a bit better than when I initially mainly used IntersectionObserver.
    const actionsTop = Math.max(0, actionsRect.top);
    const actionsBottom = Math.min(viewportHeight, actionsRect.bottom);
    const actionsVisibleHeight = Math.max(0, actionsBottom - actionsTop);

    const mainVisible = actionsVisibleHeight > 0 && actionsRect.top < viewportHeight;

    const altNeeded = actionsRect.top < 0 || actionsRect.bottom > viewportHeight;

    return { mainVisible, altNeeded };
  }

  /// Update UI based on visibility states - see note on hideMainActions.
  function updateAlternativeUI() {
    // Skip all visibility updates in preview mode
    if (elements.labelAndClaimDiv?.classList.contains("printlike")) {
      return;
    }

    const { mainVisible, altNeeded } = checkVisibilityStates();

    // In test environment, always show main actions regardless of visibility
    if (mainVisible || isTestEnvironment) {
      showMainActions();
    } else {
      hideMainActions();
    }

    if (altNeeded) {
      showAlternativeUI();
    } else {
      hideAlternativeUI();
    }
  }

  function setupResizeObserver() {
    if (!window.ResizeObserver) {
      return null;
    }

    const observer = new ResizeObserver((_entries) => {
      debouncedUpdate(100);
    });

    if (elements.labelAndClaimDiv) {
      observer.observe(elements.labelAndClaimDiv);
    }

    return observer;
  }

  function setupWindowEvents() {
    window.addEventListener("resize", () => {
      trackUserInteraction();
      debouncedUpdate(100);
    });

    window.addEventListener(
      "scroll",
      () => {
        trackUserInteraction();
        debouncedUpdate(50);
      },
      { passive: true }
    );
  }

  function setupUserInteractionTracking() {
    ["scroll", "click", "keydown", "touchstart"].forEach((eventType) => {
      window.addEventListener(eventType, trackUserInteraction, { passive: true, once: true });
    });
  }

  function setupContentObserver() {
    const contentObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      mutations.forEach((mutation) => {
        if (
          mutation.target === elements.labelAndClaimDiv ||
          elements.labelAndClaimDiv?.contains(mutation.target)
        ) {
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        debouncedUpdate(50);
      }
    });

    if (elements.labelAndClaimDiv) {
      contentObserver.observe(elements.labelAndClaimDiv, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style"],
      });
    }

    return contentObserver;
  }

  function startWhenReady() {
    const checkReady = () => {
      if (typeof window.recipeLabelSettled === "function" && window.recipeLabelSettled()) {
        state.isReady = true;
        startFullFunctionality();
      } else {
        setTimeout(checkReady, 50);
      }
    };

    // Start checking
    checkReady();
  }

  function performFullUpdate() {
    if (!state.isReady) {
      return;
    }

    // Skip all updates if in preview mode
    if (elements.labelAndClaimDiv?.classList.contains("printlike")) {
      return;
    }

    requestAnimationFrame(() => {
      try {
        updateAlternativeUI();
      } catch (error) {
        console.error("AltDownloadButton: Error during update:", error);
      }
    });
  }

  function debouncedUpdate(delay = 100) {
    clearTimeout(altDownloadButtonTimeout);
    altDownloadButtonTimeout = setTimeout(() => {
      performFullUpdate();
    }, delay);
  }

  function startFullFunctionality() {
    const resizeObserver = setupResizeObserver();
    setupWindowEvents();

    setupModalTrigger();
    setupUserInteractionTracking();

    const contentObserver = setupContentObserver();

    performFullUpdate();

    altDownloadButtonCleanup = () => {
      resizeObserver?.disconnect();
      contentObserver?.disconnect();
      clearTimeout(altDownloadButtonTimeout);
    };
  }

  function setupModalTrigger() {
    if (!elements.altTriggerButton) {
      console.warn("AltDownloadButton: Alt trigger button not found");
      return;
    }

    elements.altTriggerButton.addEventListener("click", () => {
      Modal.show("#label-actions-modal");
    });
  }

  function init() {
    if (altDownloadButtonCleanup) {
      altDownloadButtonCleanup();
    } else {
      elements.labelActionsMain.style.visibility = "visible";
      elements.labelActionsMain.style.opacity = "1";
      elements.labelActionsMain.style.pointerEvents = "auto";

      elements.altTriggerButton.style.display = "inline-block";
      elements.altTriggerButton.style.visibility = "hidden";
      elements.altTriggerButton.style.opacity = "0";
    }

    startWhenReady();
  }

  init();
}

/**
 * Intercept labeltype changes for public recipes to show modal
 * for APAP/Aggregate formats. (Leaves labeltype at original value.)
 */
function bindLabelTypeListener() {
  const labeltype = document.getElementById("labeltype");
  const labelActions = document.querySelector(".label-actions");

  if (!labeltype || !labelActions) {
    return;
  }

  const isPublicRecipe = labelActions.dataset.recipePublic === "true";

  if (!isPublicRecipe) {
    return;
  }

  let originalValue = labeltype.value;

  const restrictedFormats = [
    "fda/adult/vertical/as_prepared",
    "fda/adult/vertical/aggregate",
    "canada/adult/vertical/as_prepared",
    "canada/adult/vertical/aggregate",
    "legacy_fda/adult/vertical/as_prepared",
  ];

  labeltype.addEventListener(
    "change",
    function (event) {
      const selectedFormat = this.value;

      if (restrictedFormats.includes(selectedFormat)) {
        // Capture the label text before reverting
        const selectedOption = this.options[this.selectedIndex];
        const formatLabel = selectedOption ? selectedOption.textContent : selectedFormat;

        // Prevent the change by reverting to original value
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.value = originalValue;

        // Update modal title with the exact format name
        const modalTitle = document.querySelector("#apap-aggregate-modal .modal-card-title");
        if (modalTitle) {
          modalTitle.textContent = formatLabel;
        }

        // Show/hide appropriate blog post links based on selected format
        document.querySelectorAll(".format-specific-link").forEach((link) => {
          const formats = link.dataset.formats.split(",");

          // Only show the link that exactly matches the selected format
          if (formats.includes(selectedFormat)) {
            link.style.display = "inline";
          } else {
            link.style.display = "none";
          }
        });

        Modal.show("#apap-aggregate-modal");

        // Trigger change event with original value to ensure UI consistency
        const revertEvent = new Event("change", { bubbles: true, cancelable: true });
        this.dispatchEvent(revertEvent);
      } else {
        // Update the stored original value for valid changes
        originalValue = this.value;
      }
    },
    true // Use capture phase to intercept before other handlers
  );
}

function bindLoadLogos(container) {
  once(container, "modal-logos", (ev) => {
    container.addEventListener(
      "modal:open",
      ({
        detail: {
          modal: { id },
        },
      }) => {
        if (id !== "logo-modal") return;

        container.querySelectorAll('[data-partial="logos/svg"]').forEach((logo_el) => {
          window.Rails.ajax({
            url: logo_el.dataset.url,
            type: "GET",
            dataType: "script",
          });
        });
      }
    );
  });
}

const scan = () => {
  bindStashedFileListeners();
  bindAnalytics();
  bindDimensions();
  bindPreviewButton();
  bindZoomValidation();
  bindAltDownloadButton();
  bindLabelTypeListener();
  initNameModal();

  document.querySelectorAll(".label-actions").forEach((container) => {
    bindLabelActions(container);
  });

  bindLoadLogos(document);

  document.addEventListener("partial:replace", bindPreviewButton);
};

onViewLoad("recipes#show", scan);
